import type React from 'react';
import classes from './reports-page.module.scss';
import { currentMonth, formatMonth, formatDate } from './reports-page.utils';
import { useReportsPageController } from './useReportsPageController';

const Status = ({ value, yes, no }: { value: boolean; yes: string; no: string }) => (
  <span className={`reportStatus ${value ? 'yes' : 'no'}`}>{value ? yes : no}</span>
);

export const ReportsPage: React.FC = () => {
  const {
    mode,
    setMode,
    month,
    setMonth,
    minimumWorkedDays,
    setMinimumWorkedDays,
    detailed,
    summary,
    isLoading,
    error,
    filteredMonths,
  } = useReportsPageController();

  return (
    <section className="reportsPage" dir="rtl">
      <div className="reportsHeader">
        <div>
          <h1>דוחות</h1>
          <p>נתוני הזמינות והשיבוצים האישיים שלך</p>
        </div>
        <div className={classes.reportsModeSwitch} role="tablist" aria-label="סוג דוח">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'detailed'}
            className={mode === 'detailed' ? 'active' : ''}
            onClick={() => setMode('detailed')}
          >
            דוח מפורט
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'summary'}
            className={mode === 'summary' ? 'active' : ''}
            onClick={() => setMode('summary')}
          >
            דוח מסכם
          </button>
        </div>
      </div>

      {mode === 'detailed' ? (
        <div className={classes.reportsToolbar}>
          <label htmlFor="report-month">חודש</label>
          <input
            id="report-month"
            type="month"
            value={month}
            max={currentMonth()}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
      ) : (
        <div className={classes.reportsToolbar}>
          <label htmlFor="minimum-worked-days">מינימום ימי עבודה בחודש</label>
          <input
            id="minimum-worked-days"
            type="number"
            min="0"
            max="31"
            value={minimumWorkedDays}
            onChange={(event) => setMinimumWorkedDays(Math.max(0, Number(event.target.value) || 0))}
          />
        </div>
      )}

      {isLoading && <p className="reportsState">טוען דוח...</p>}
      {error && <p className="errorText reportsState">{error}</p>}

      {!isLoading && !error && mode === 'detailed' && detailed && (
        <>
          <div className="reportSummaryStrip" aria-label="סיכום חודשי">
            <div>
              <span>הגיש זמינות</span>
              <strong>{detailed.totals.submittedDays}</strong>
            </div>
            <div>
              <span>שובץ</span>
              <strong>{detailed.totals.assignedDays}</strong>
            </div>
            <div>
              <span>עבד</span>
              <strong>{detailed.totals.workedDays}</strong>
            </div>
          </div>
          <div className="reportTableWrap">
            <table role="table" className="reportTable">
              <caption>
                {detailed.employeeName} · {formatMonth(detailed.month)}
              </caption>
              <thead>
                <tr>
                  <th>תאריך</th>
                  <th>הגיש זמינות</th>
                  <th>שובץ</th>
                  <th>עבד</th>
                  <th>פרטי משמרת</th>
                </tr>
              </thead>
              <tbody>
                {detailed.days.map((day) => (
                  <tr key={day.dateDay}>
                    <td data-label="תאריך">{formatDate(day.dateDay)}</td>
                    <td data-label="הגיש זמינות">
                      <Status value={day.submittedAvailability} yes="כן" no="לא" />
                    </td>
                    <td data-label="שובץ">
                      <Status value={day.assigned} yes="כן" no="לא" />
                    </td>
                    <td data-label="עבד">
                      <Status value={day.worked} yes="כן" no="לא" />
                    </td>
                    <td data-label="פרטי משמרת">
                      {day.assignments.length > 0
                        ? day.assignments
                            .map(
                              (assignment) =>
                                `${assignment.shiftName} · ${assignment.roleName} · ${assignment.startHour}-${assignment.endHour}`,
                            )
                            .join(' | ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {detailed.days.length === 0 && <p className="reportsState">אין ימים שהתרחשו בחודש הזה.</p>}
          </div>
          <p className="reportsFootnote">
            „עבד” בדוח האישי הוותיק מבוסס על שיבוץ ביום שהסתיים. שעות נוכחות בפועל מוצגות במסך נוכחות.
          </p>
        </>
      )}

      {!isLoading && !error && mode === 'summary' && summary && (
        <div className="reportTableWrap">
          <table role="table" className="reportTable">
            <caption>{summary.employeeName} · סיכום חודשי</caption>
            <thead>
              <tr>
                <th>חודש</th>
                <th>ימי זמינות</th>
                <th>ימי שיבוץ</th>
                <th>ימי עבודה</th>
              </tr>
            </thead>
            <tbody>
              {filteredMonths.map((row) => (
                <tr key={row.month}>
                  <td data-label="חודש">{formatMonth(row.month)}</td>
                  <td data-label="ימי זמינות">{row.submittedDays}</td>
                  <td data-label="ימי שיבוץ">{row.assignedDays}</td>
                  <td data-label="ימי עבודה">
                    <strong>{row.workedDays}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredMonths.length === 0 && <p className="reportsState">אין חודשים שתואמים לסינון.</p>}
        </div>
      )}
    </section>
  );
};
